import { useState } from 'react';
import { pushDataLayer } from '@/lib/gtm';
import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/data/translations';
import { submitGeneralContact } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle } from 'lucide-react';

export function ContactForm() {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await submitGeneralContact({
      type: 'general_contact',
      ...formData,
      language
    });
    
    if (result.success) {
      pushDataLayer({
        event: 'form_submit',
        form_id: 'contact_form',
        form_name: 'General Contact',
      });
      toast.success(
        language === 'de' ? 'Nachricht gesendet!' : 
        language === 'it' ? 'Messaggio inviato!' : 
        'Message sent!', 
        {
          description: language === 'de' ? 'Wir melden uns bald bei Ihnen.' :
                       language === 'it' ? 'Vi contatteremo presto.' :
                       'We will get back to you soon.'
        }
      );
      
      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Reset success state after 5 seconds
      setTimeout(() => setIsSuccess(false), 5000);
    } else {
      toast.error(
        language === 'de' ? 'Fehler beim Senden' : 
        language === 'it' ? 'Errore nell\'invio' : 
        'Failed to send message', 
        {
          description: language === 'de' ? 'Bitte versuchen Sie es später erneut.' :
                       language === 'it' ? 'Per favore riprova più tardi.' :
                       'Please try again later.'
        }
      );
    }
    
    setIsSubmitting(false);
  };

  if (isSuccess) {
    return (
      <div className="text-center py-12" data-testid="contact-form-success">
        <CheckCircle className="h-16 w-16 text-[#4A5D4F] mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl text-[#2C2C2C] mb-2">
          {language === 'de' ? 'Vielen Dank!' : 
           language === 'it' ? 'Grazie!' : 
           'Thank you!'}
        </h3>
        <p className="font-sans text-[#5D5D5D]">
          {language === 'de' ? 'Ihre Nachricht wurde erfolgreich gesendet.' : 
           language === 'it' ? 'Il tuo messaggio è stato inviato con successo.' : 
           'Your message has been sent successfully.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className="font-sans text-sm text-[#5D5D5D]">
            {t(translations.contact.name)} *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="bg-transparent border-0 border-b border-black/20 focus:border-black rounded-none px-0 py-4 placeholder:text-black/40 focus:ring-0 focus-visible:ring-0"
            placeholder={language === 'de' ? 'Ihr Name' : language === 'it' ? 'Il tuo nome' : 'Your name'}
            data-testid="input-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="font-sans text-sm text-[#5D5D5D]">
            {t(translations.contact.email)} *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="bg-transparent border-0 border-b border-black/20 focus:border-black rounded-none px-0 py-4 placeholder:text-black/40 focus:ring-0 focus-visible:ring-0"
            placeholder="your@email.com"
            data-testid="input-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="phone" className="font-sans text-sm text-[#5D5D5D]">
            {t(translations.contact.phone)}
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            className="bg-transparent border-0 border-b border-black/20 focus:border-black rounded-none px-0 py-4 placeholder:text-black/40 focus:ring-0 focus-visible:ring-0"
            placeholder="+39 335 691 7918"
            data-testid="input-phone"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="subject" className="font-sans text-sm text-[#5D5D5D]">
            {t(translations.contact.subject)}
          </Label>
          <Input
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="bg-transparent border-0 border-b border-black/20 focus:border-black rounded-none px-0 py-4 placeholder:text-black/40 focus:ring-0 focus-visible:ring-0"
            placeholder={language === 'de' ? 'Worum geht es?' : language === 'it' ? 'Di cosa si tratta?' : 'What is this about?'}
            data-testid="input-subject"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message" className="font-sans text-sm text-[#5D5D5D]">
          {t(translations.contact.message)} *
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="bg-transparent border-0 border-b border-black/20 focus:border-black rounded-none px-0 py-4 placeholder:text-black/40 focus:ring-0 focus-visible:ring-0 resize-none"
          placeholder={language === 'de' ? 'Ihre Nachricht...' : language === 'it' ? 'Il tuo messaggio...' : 'Your message...'}
          data-testid="input-message"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="rounded-full bg-[#2C2C2C] text-white px-8 py-4 h-auto hover:bg-[#4A5D4F] transition-colors duration-300 text-sm uppercase tracking-widest"
        data-testid="submit-button"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {language === 'de' ? 'Senden...' : language === 'it' ? 'Invio...' : 'Sending...'}
          </>
        ) : (
          t(translations.contact.send)
        )}
      </Button>
    </form>
  );
}
